import React, { useEffect, useState } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { Link } from "react-router-dom";
import ExportPopup from "./export";

interface AdminData {
  id: number;
  name: string;
  email: string;
  no_tlpn?: string;
  role: string;
  foto_profile?: string;
  is_confirm: string;
  created_at: string;
}

interface MetaData {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

const AdminStafAktifasi: React.FC = () => {
  const [adminList, setAdminList] = useState<AdminData[]>([]);
  const [filteredList, setFilteredList] = useState<AdminData[]>([]);
  const [meta, setMeta] = useState<MetaData | null>(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [isExportOpen, setIsExportOpen] = useState(false);

  // Pagination lokal
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Fetch data admin
  const fetchAdmin = async () => {
    setLoading(true);
    try {
      const token = Cookies.get("token");
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/adminstaf`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // ✅ Data sesuai contoh JSON kamu
      const data = Array.isArray(res.data.data) ? res.data.data : [];
      const metaData = res.data.meta || null;

      setAdminList(data);
      setFilteredList(data);
      setMeta(metaData);
    } catch (err) {
      console.error("Gagal ambil data admin:", err);
      setAdminList([]);
      setFilteredList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmin();
  }, []);

  // Filter berdasarkan pencarian
  useEffect(() => {
    const result = adminList.filter((item) => item.name.toLowerCase().includes(search.toLowerCase()) || item.email.toLowerCase().includes(search.toLowerCase()) || item.no_tlpn?.toLowerCase().includes(search.toLowerCase()));
    setFilteredList(result);
    setCurrentPage(1);
  }, [search, adminList]);

  // Pagination manual di sisi frontend
  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentData = filteredList.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredList.length / itemsPerPage) || 1;

  return (
    <div className="overflow-x-auto mt-7">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-center gap-3 mb-4">
        <h2 className="text-xl font-bold">Daftar Admin Kementerian</h2>

        <div className="flex gap-3 items-center">
          <input
            type="text"
            placeholder="Cari nama, email, atau no telepon..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-yellow-400 outline-none w-60"
          />

          <button onClick={() => setIsExportOpen(true)} className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition-all duration-300 shadow-md">
            Export
          </button>
        </div>
      </div>

      {/* Table */}
      <table className="min-w-full border border-gray-300 text-sm md:text-base rounded-lg overflow-hidden">
        <thead className="bg-primary text-gray-800">
          <tr>
            <th className="p-2 text-left">No</th>
            <th className="p-2 text-left">Nama</th>
            <th className="p-2 text-left">Email</th>
            <th className="p-2 text-left">No Telepon</th>
            <th className="p-2 text-left">Role</th>
            <th className="p-2 text-left">Status</th>
            <th className="p-2 text-left">Aksi</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={7} className="text-center p-4">
                <div className="flex justify-center items-center h-40">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-yellow-500"></div>
                </div>
              </td>
            </tr>
          ) : currentData.length > 0 ? (
            currentData.map((item, index) => (
              <tr key={item.id} className="border-b hover:bg-gray-100 transition-all duration-200">
                <td className="p-2">{indexOfFirst + index + 1}</td>
                <td className="p-2 flex items-center gap-2">
                  <img src={item.foto_profile} alt={item.name} className="w-8 h-8 rounded-full object-cover border" />
                  {item.name}
                </td>
                <td className="p-2">{item.email}</td>
                <td className="p-2">{item.no_tlpn || "-"}</td>
                <td className="p-2">{item.role}</td>
                <td className="p-2">
                  {item.is_confirm === "1" ? <span className="px-3 py-1 rounded-full bg-green-500 text-white text-xs">Aktif</span> : <span className="px-3 py-1 rounded-full bg-red-500 text-white text-xs">Tidak Aktif</span>}
                </td>
                <td className="p-2 text-yellow-600">
                  <Link to={`/dashboard/akun/detail/${item.id}`}>Detail</Link>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={7} className="text-center p-4">
                Data tidak ditemukan
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Pagination */}
      <div className="flex justify-end items-center mt-4 gap-2">
        <button disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)} className="px-3 py-1 border rounded disabled:opacity-50">
          Prev
        </button>

        <span className="text-sm text-gray-700">
          {currentPage} of {totalPages}
        </span>

        <button disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => p + 1)} className="px-3 py-1 border rounded disabled:opacity-50">
          Next
        </button>
      </div>

      {/* Export Popup */}
      {isExportOpen && <ExportPopup total={filteredList.length} data={filteredList} onClose={() => setIsExportOpen(false)} />}
    </div>
  );
};

export default AdminStafAktifasi;
